import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi, subscriptionsApi, keysApi, billingApi, pushApi, guildPoliciesApi } from '~/lib/api';
import { useState, useEffect } from 'react';
import { clsx } from 'clsx';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { usePushNotifications } from '~/hooks/usePushNotifications';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '~/contexts/AuthContext';

/**
 * Format relative time
 */
function formatTimeAgo(timestamp: string | null): string {
  if (!timestamp) return 'never';

  const diffMs = Date.now() - new Date(timestamp).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function SettingsPageContent() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [formValues, setFormValues] = useState({
    currency: 'CAD',
    confidence_threshold: '70',
  });
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);

  // Push notifications
  const push = usePushNotifications();

  // Billing status
  const { data: billingData, isLoading: billingLoading } = useQuery({
    queryKey: ['billing'],
    queryFn: billingApi.status,
  });

  const hasActiveSubscription = billingData?.isActive ?? false;
  // Premium granted by a Discord role rather than Stripe — no customer/portal.
  const isBypass = billingData?.bypass ?? false;

  // Alert schedule preference
  const { data: scheduleData } = useQuery({
    queryKey: ['push', 'schedule'],
    queryFn: pushApi.getSchedule,
    enabled: hasActiveSubscription,
  });
  const scheduleMutation = useMutation({
    mutationFn: pushApi.setSchedule,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['push', 'schedule'] }),
  });

  // Handle billing redirect params
  const billingParam = searchParams.get('billing');
  useEffect(() => {
    if (billingParam) {
      // Clear the param from URL
      setSearchParams((prev) => {
        prev.delete('billing');
        return prev;
      });
      // Refresh billing status
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    }
  }, [billingParam, setSearchParams, queryClient]);

  // Fetch settings
  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.get,
  });

  // Fetch subscriptions
  const { data: subscriptionsData } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: subscriptionsApi.list,
  });

  // Fetch API keys
  const { data: keysData } = useQuery({
    queryKey: ['apiKeys'],
    queryFn: keysApi.list,
  });

  // Update form when settings load
  useEffect(() => {
    if (settingsData?.settings) {
      setFormValues((prev) => ({
        ...prev,
        ...settingsData.settings,
      }));
    }
  }, [settingsData]);

  // Update settings mutation
  const updateMutation = useMutation({
    mutationFn: settingsApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });

  // Delete subscription mutation
  const deleteSubMutation = useMutation({
    mutationFn: subscriptionsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  // Toggle channel_type between 'plays' and 'alerts'. plays run through
  // the LLM for signal extraction; alerts are archived commentary.
  const updateSubTypeMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: 'plays' | 'alerts' }) =>
      subscriptionsApi.updateType(id, type),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
    },
  });

  // Create API key mutation
  const createKeyMutation = useMutation({
    mutationFn: (name: string) => keysApi.create(name),
    onSuccess: (data) => {
      setNewKeyValue(data.key);
      setNewKeyName('');
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });

  // Delete API key mutation
  const deleteKeyMutation = useMutation({
    mutationFn: keysApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
    },
  });

  // Billing mutations
  const checkoutMutation = useMutation({
    mutationFn: billingApi.createCheckout,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const portalMutation = useMutation({
    mutationFn: billingApi.createPortal,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) return;
    await createKeyMutation.mutateAsync(newKeyName.trim());
  };

  const handleChange = (key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    await updateMutation.mutateAsync(formValues);
  };

  const subscriptions = subscriptionsData?.subscriptions || [];
  const extensionConnected = subscriptionsData?.extension_connected ?? false;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Settings</h1>
        <p className="text-text-secondary">Configure your preferences</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      ) : (
        <>
          {/* Signal Settings - Admin only */}
          {isAdmin && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-text-primary mb-4">Signal Settings</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-wider text-text-secondary mb-1.5">
                    Confidence Threshold
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formValues.confidence_threshold}
                    onChange={(e) =>
                      handleChange('confidence_threshold', e.target.value)
                    }
                    className="input w-32"
                  />
                  <p className="text-[11px] text-text-muted mt-1.5">
                    Only show signals with confidence above this percentage.
                  </p>
                </div>

              </div>
            </div>
          )}

          {/* Notifications - visible to all users */}

          {/* Notifications */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Notifications</h2>
            <div className="space-y-4">
              {!hasActiveSubscription ? (
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-text-primary">Push Notifications</div>
                    <p className="text-sm text-text-secondary">
                      Get notified when new trading signals arrive, even when the app is closed
                    </p>
                    <p className="text-sm text-warning mt-1">
                      Upgrade to enable push notifications
                    </p>
                  </div>
                  <button
                    onClick={() => checkoutMutation.mutate()}
                    disabled={checkoutMutation.isPending}
                    className="px-4 py-2 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-gray-900 text-sm font-semibold rounded-lg transition-all shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40"
                  >
                    {checkoutMutation.isPending ? 'Loading...' : 'Upgrade'}
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-text-primary">Push Notifications</div>
                      <p className="text-sm text-text-secondary">
                        Get notified when new trading signals arrive, even when the app is closed
                      </p>
                      {!push.supported && (
                        <p className="text-sm text-warning mt-1">
                          Push notifications are not supported in this browser
                        </p>
                      )}
                      {push.permission === 'denied' && push.supported && (
                        <p className="text-sm text-loss mt-1">
                          Notifications are blocked. Enable them in your browser settings.
                        </p>
                      )}
                      {push.error && push.supported && (
                        <p className="text-sm text-loss mt-1">{push.error}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {push.subscribed && (
                        <span className="text-sm text-profit">Enabled</span>
                      )}
                      <button
                        onClick={push.subscribed ? push.unsubscribe : push.subscribe}
                        disabled={!push.supported || push.loading || push.permission === 'denied'}
                        className={clsx(
                          'btn',
                          push.subscribed ? 'btn-secondary' : 'btn-primary',
                          (!push.supported || push.permission === 'denied') && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {push.loading
                          ? 'Loading...'
                          : push.subscribed
                            ? 'Disable'
                            : 'Enable Notifications'}
                      </button>
                    </div>
                  </div>

                  {/* Alert schedule toggle */}
                  {hasActiveSubscription && (
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-text-primary">All-hours alerts</div>
                        <p className="text-sm text-text-secondary">
                          Receive alerts outside market hours (9 AM – 4:30 PM ET)
                        </p>
                      </div>
                      <button
                        role="switch"
                        aria-checked={scheduleData?.alertSchedule === 'all_time'}
                        onClick={() =>
                          scheduleMutation.mutate(
                            scheduleData?.alertSchedule === 'all_time' ? 'market_hours' : 'all_time'
                          )
                        }
                        disabled={scheduleMutation.isPending}
                        className={clsx(
                          'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
                          scheduleData?.alertSchedule === 'all_time'
                            ? 'bg-accent/30'
                            : 'bg-surface-3'
                        )}
                      >
                        <span
                          className={clsx(
                            'pointer-events-none inline-block h-5 w-5 rounded-full shadow transform ring-0 transition duration-200 ease-in-out',
                            scheduleData?.alertSchedule === 'all_time'
                              ? 'translate-x-5 bg-accent'
                              : 'translate-x-0 bg-text-muted'
                          )}
                        />
                      </button>
                    </div>
                  )}

                  {/* iOS/Safari instructions */}
                  {push.supported && !push.subscribed && (
                    <div className="bg-surface-3 rounded-lg p-4 text-sm">
                      <p className="text-text-primary font-medium mb-2">Mobile Setup</p>
                      <ul className="text-text-secondary space-y-1 list-disc list-inside">
                        <li>On iOS: Add this app to your Home Screen first (Share → Add to Home Screen)</li>
                        <li>On Android: Install the app when prompted, or use the browser menu</li>
                        <li>Push notifications require HTTPS in production</li>
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Subscription - visible to all users */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Subscription</h2>
            {billingLoading ? (
              <div className="flex items-center gap-2 text-text-secondary">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accent"></div>
                <span>Loading billing status...</span>
              </div>
            ) : isBypass ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-[11px] font-mono font-medium uppercase tracking-wider bg-profit/10 text-profit">
                    ✓ Premium via Discord role
                  </span>
                </div>
                <div className="bg-surface-3 rounded-lg p-4 space-y-2">
                  <p className="text-text-secondary text-sm">
                    Your Discord role unlocks premium for free — no subscription needed. Full history access and push notifications are enabled.
                  </p>
                </div>
              </div>
            ) : hasActiveSubscription ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded text-[11px] font-mono font-medium uppercase tracking-wider bg-profit/10 text-profit">
                    ✓ Premium Active
                  </span>
                  <span className="text-text-secondary text-sm">
                    {billingData?.subscription?.cancelAtPeriodEnd
                      ? 'Cancels'
                      : 'Renews'}{' '}
                    {billingData?.subscription?.currentPeriodEnd &&
                      new Date(billingData.subscription.currentPeriodEnd).toLocaleDateString()}
                  </span>
                </div>
                <div className="bg-surface-3 rounded-lg p-4 space-y-2">
                  <p className="text-text-primary font-medium">Your benefits:</p>
                  <ul className="text-sm text-text-secondary space-y-1">
                    <li className="flex items-center gap-2">
                      <span className="text-profit">✓</span> Full trading history access
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="text-profit">✓</span> Push notifications for new signals
                    </li>
                  </ul>
                </div>
                <button
                  onClick={() => portalMutation.mutate()}
                  disabled={portalMutation.isPending}
                  className="btn btn-secondary"
                >
                  {portalMutation.isPending ? 'Loading...' : 'Manage Subscription'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-yellow-500/10 via-yellow-400/10 to-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-text-primary font-medium mb-3">Unlock Premium Features:</p>
                  <ul className="text-sm space-y-2 mb-4">
                    <li className="flex items-center gap-2 text-text-secondary">
                      <span className="text-warning">⭐</span>
                      <span><span className="text-text-primary font-medium">Full History</span> — Access all past trades, not just today</span>
                    </li>
                    <li className="flex items-center gap-2 text-text-secondary">
                      <span className="text-warning">🔔</span>
                      <span><span className="text-text-primary font-medium">Push Notifications</span> — Instant alerts on new signals</span>
                    </li>
                  </ul>
                  <button
                    onClick={() => checkoutMutation.mutate()}
                    disabled={checkoutMutation.isPending}
                    className="w-full px-6 py-3 bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-300 hover:to-yellow-400 text-gray-900 font-bold rounded-lg transition-all shadow-lg shadow-yellow-500/25 hover:shadow-yellow-500/40 hover:scale-[1.02]"
                  >
                    {checkoutMutation.isPending ? 'Loading...' : 'Upgrade Now'}
                  </button>
                </div>
                {checkoutMutation.isError && (
                  <p className="text-loss text-sm">
                    Failed to start checkout. Please try again.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Admin-only sections below */}
          {isAdmin && (
            <>
          {/* Display Settings */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Display</h2>
            <div>
              <label className="block text-[11px] font-mono uppercase tracking-wider text-text-secondary mb-1.5">Currency</label>
              <select
                value={formValues.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="input"
              >
                <option value="CAD">CAD - Canadian Dollar</option>
                <option value="USD">USD - US Dollar</option>
              </select>
            </div>
          </div>

          <GuildPoliciesSection />

          {/* Manual per-user grants moved to the admin Users page */}
          <Link to="/settings/users" className="card p-6 block hover:bg-surface-2 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-text-primary mb-1">Manual Guild Grants</h2>
                <p className="text-sm text-text-secondary">
                  Grant specific users access independent of Discord membership — now managed per user.
                </p>
              </div>
              <span className="text-text-secondary shrink-0 ml-4">Open Users →</span>
            </div>
          </Link>

          {/* API Keys */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">API Keys</h2>
            <p className="text-sm text-text-secondary mb-4">
              API keys are used to authenticate the Chrome extension with the backend.
            </p>

            {/* New key display */}
            {newKeyValue && (
              <div className="mb-4 p-4 bg-profit/10 border border-profit/30 rounded-lg">
                <p className="text-profit font-medium mb-2">
                  New API Key Created - Copy it now!
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-surface-3 p-2 rounded text-sm text-text-primary font-mono break-all">
                    {newKeyValue}
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(newKeyValue);
                    }}
                    className="btn btn-secondary text-sm"
                  >
                    Copy
                  </button>
                </div>
                <p className="text-warning text-xs mt-2">
                  This key will not be shown again. Save it somewhere safe.
                </p>
                <button
                  onClick={() => setNewKeyValue(null)}
                  className="text-text-muted text-sm mt-2 hover:text-text-primary"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Existing keys */}
            {keysData?.keys && keysData.keys.length > 0 ? (
              <div className="space-y-2 mb-4">
                {keysData.keys.map((apiKey) => (
                  <div
                    key={apiKey.id}
                    className="flex items-center justify-between p-3 bg-surface-3 rounded-lg border border-border"
                  >
                    <div>
                      <div className="font-medium text-text-primary">{apiKey.name}</div>
                      <div className="text-sm text-text-secondary">
                        Created {formatTimeAgo(apiKey.created_at)}
                        {apiKey.last_used_at && ` · Last used ${formatTimeAgo(apiKey.last_used_at)}`}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteKeyMutation.mutate(apiKey.id)}
                      disabled={deleteKeyMutation.isPending}
                      className="text-loss hover:text-loss/80 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-text-muted mb-4">No API keys created yet.</p>
            )}

            {/* Create new key */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="Key name (e.g., Chrome Extension)"
                className="input flex-1"
              />
              <button
                onClick={handleCreateKey}
                disabled={!newKeyName.trim() || createKeyMutation.isPending}
                className="btn btn-primary"
              >
                {createKeyMutation.isPending ? 'Creating...' : 'Create Key'}
              </button>
            </div>
          </div>

          {/* Subscriptions */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-text-primary">
                Watched Channels ({subscriptions.length})
              </h2>
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={clsx(
                    'w-2 h-2 rounded-full',
                    extensionConnected ? 'bg-accent pulse-live' : 'bg-loss'
                  )}
                />
                <span className={extensionConnected ? 'text-profit' : 'text-loss'}>
                  {extensionConnected ? 'Extension connected' : 'Extension offline'}
                </span>
              </div>
            </div>
            {subscriptions.length === 0 ? (
              <p className="text-text-secondary">
                No channels configured. Use the Chrome extension to add channels.
              </p>
            ) : (
              <div className="space-y-2">
                {subscriptions.map((sub) => {
                  const lastActivity = formatTimeAgo(sub.last_message_at);
                  const activityLabel = sub.message_count > 0
                    ? `Last message ${lastActivity} · ${sub.message_count} total`
                    : 'No messages yet';

                  return (
                    <div
                      key={sub.id}
                      className={clsx(
                        'flex items-center justify-between p-3 rounded-lg border border-border',
                        sub.is_watching ? 'bg-surface-3' : 'bg-surface-1'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={clsx(
                            'w-2 h-2 rounded-full',
                            sub.is_watching ? 'bg-accent pulse-live' : 'bg-loss'
                          )}
                          title={sub.is_watching ? 'Discord tab is open' : 'Discord tab not open'}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-text-primary">
                              #{sub.channel_name}
                            </span>
                            {sub.is_watching ? (
                              <span className="text-xs text-profit">watching</span>
                            ) : (
                              <span className="text-xs text-loss">not watching</span>
                            )}
                            <span
                              className={clsx(
                                'text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded',
                                sub.channel_type === 'alerts'
                                  ? 'bg-warning/15 text-warning'
                                  : 'bg-accent/15 text-accent'
                              )}
                            >
                              {sub.channel_type}
                            </span>
                          </div>
                          <div className="text-sm text-text-secondary">
                            {activityLabel}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() =>
                            updateSubTypeMutation.mutate({
                              id: sub.id,
                              type: sub.channel_type === 'plays' ? 'alerts' : 'plays',
                            })
                          }
                          disabled={updateSubTypeMutation.isPending}
                          className="text-accent hover:text-accent/80 text-sm disabled:opacity-50"
                          title={
                            sub.channel_type === 'plays'
                              ? 'Flip to alerts: messages stored as commentary, skip LLM parsing'
                              : 'Flip to plays: messages go through the LLM signal parser'
                          }
                        >
                          Flip to {sub.channel_type === 'plays' ? 'alerts' : 'plays'}
                        </button>
                        <button
                          onClick={() => deleteSubMutation.mutate(sub.id)}
                          className="text-loss hover:text-loss/80 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-sm text-text-muted mt-4">
              Channels are added via the Chrome extension. Green = Discord tab is open.
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={updateMutation.isPending}
              className="btn btn-primary"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
            </button>
          </div>

          {updateMutation.isSuccess && (
            <div className="bg-profit/10 border border-profit/30 rounded-lg p-4">
              <p className="text-profit">Settings saved successfully!</p>
            </div>
          )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function GuildPoliciesSection() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['guildPolicies'],
    queryFn: guildPoliciesApi.list,
  });

  const [newGuildId, setNewGuildId] = useState('');
  const [newGuildName, setNewGuildName] = useState('');
  const [newRoles, setNewRoles] = useState('');
  const [newBypassRoles, setNewBypassRoles] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRoles, setEditRoles] = useState('');
  const [editBypassRoles, setEditBypassRoles] = useState('');
  const [editName, setEditName] = useState('');

  const createMutation = useMutation({
    mutationFn: guildPoliciesApi.create,
    onSuccess: () => {
      setNewGuildId('');
      setNewGuildName('');
      setNewRoles('');
      setNewBypassRoles('');
      queryClient.invalidateQueries({ queryKey: ['guildPolicies'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ guildId, data }: { guildId: string; data: Parameters<typeof guildPoliciesApi.update>[1] }) =>
      guildPoliciesApi.update(guildId, data),
    onSuccess: () => {
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ['guildPolicies'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: guildPoliciesApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['guildPolicies'] }),
  });

  const policies = data?.policies ?? [];

  return (
    <div className="card p-6">
      <h2 className="text-lg font-semibold text-text-primary mb-1">Guild Access Policies</h2>
      <p className="text-sm text-text-secondary mb-4">
        Discord guilds whose members can log in. <strong>Login roles</strong>: empty = any guild member, else at least one required.
        {' '}<strong>Premium-bypass roles</strong>: members with one of these get premium free (no Stripe). The two lists are independent.
      </p>

      {isLoading ? (
        <p className="text-text-muted">Loading...</p>
      ) : (
        <div className="space-y-2 mb-6">
          {policies.length === 0 && (
            <p className="text-text-muted">No policies configured. Login will fail until at least one guild is allowed.</p>
          )}
          {policies.map((p) => {
            const isEditing = editingId === p.guild_id;
            return (
              <div key={p.guild_id} className="p-3 bg-surface-3 rounded-lg border border-border">
                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="input w-full"
                      placeholder="Guild name"
                    />
                    <input
                      type="text"
                      value={editRoles}
                      onChange={(e) => setEditRoles(e.target.value)}
                      className="input w-full font-mono text-sm"
                      placeholder="Login role IDs (empty = any member)"
                    />
                    <input
                      type="text"
                      value={editBypassRoles}
                      onChange={(e) => setEditBypassRoles(e.target.value)}
                      className="input w-full font-mono text-sm"
                      placeholder="Premium-bypass role IDs (empty = none)"
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => setEditingId(null)}
                        className="btn btn-secondary text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() =>
                          updateMutation.mutate({
                            guildId: p.guild_id,
                            data: { guild_name: editName, required_role_ids: editRoles, premium_bypass_role_ids: editBypassRoles },
                          })
                        }
                        disabled={updateMutation.isPending}
                        className="btn btn-primary text-sm"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-text-primary">{p.guild_name}</span>
                        {p.enabled ? (
                          <span className="text-xs px-1.5 py-0.5 bg-profit/20 text-profit rounded">enabled</span>
                        ) : (
                          <span className="text-xs px-1.5 py-0.5 bg-surface-2 text-text-muted rounded">disabled</span>
                        )}
                      </div>
                      <div className="text-xs text-text-secondary font-mono mt-0.5 truncate">
                        {p.guild_id}
                      </div>
                      <div className="text-xs text-text-muted mt-0.5">
                        Login roles: {p.required_role_ids ? <code className="font-mono">{p.required_role_ids}</code> : <span className="italic">any member</span>}
                      </div>
                      <div className="text-xs text-text-muted mt-0.5">
                        Premium bypass: {p.premium_bypass_role_ids
                          ? <code className="font-mono text-warning">{p.premium_bypass_role_ids}</code>
                          : <span className="italic">none</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() =>
                          updateMutation.mutate({
                            guildId: p.guild_id,
                            data: { enabled: !p.enabled },
                          })
                        }
                        className="text-sm text-text-secondary hover:text-text-primary"
                      >
                        {p.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => {
                          setEditingId(p.guild_id);
                          setEditName(p.guild_name);
                          setEditRoles(p.required_role_ids);
                          setEditBypassRoles(p.premium_bypass_role_ids);
                        }}
                        className="text-sm text-text-secondary hover:text-text-primary"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`Remove access for ${p.guild_name}?`)) {
                            deleteMutation.mutate(p.guild_id);
                          }
                        }}
                        className="text-sm text-loss hover:text-loss/80"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="border-t border-border pt-4">
        <h3 className="text-sm font-semibold text-text-primary mb-2">Add Guild</h3>
        <div className="space-y-2">
          <input
            type="text"
            value={newGuildId}
            onChange={(e) => setNewGuildId(e.target.value)}
            placeholder="Guild ID (Discord snowflake)"
            className="input w-full font-mono text-sm"
          />
          <input
            type="text"
            value={newGuildName}
            onChange={(e) => setNewGuildName(e.target.value)}
            placeholder="Guild name (display)"
            className="input w-full"
          />
          <input
            type="text"
            value={newRoles}
            onChange={(e) => setNewRoles(e.target.value)}
            placeholder="Login role IDs, comma-separated (empty = any member)"
            className="input w-full font-mono text-sm"
          />
          <input
            type="text"
            value={newBypassRoles}
            onChange={(e) => setNewBypassRoles(e.target.value)}
            placeholder="Premium-bypass role IDs, comma-separated (empty = none)"
            className="input w-full font-mono text-sm"
          />
          <button
            onClick={() =>
              createMutation.mutate({
                guild_id: newGuildId,
                guild_name: newGuildName,
                required_role_ids: newRoles,
                premium_bypass_role_ids: newBypassRoles,
              })
            }
            disabled={createMutation.isPending || !newGuildId || !newGuildName}
            className="btn btn-primary text-sm"
          >
            {createMutation.isPending ? 'Adding...' : 'Add Guild'}
          </button>
          {createMutation.isError && (
            <p className="text-loss text-sm">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to add guild'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <ErrorBoundary>
      <SettingsPageContent />
    </ErrorBoundary>
  );
}
